import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import Dropdown from "../../components/Dropdown";
import HotdropdownV2 from "../../components/HotdropdownV2";
import { getAllEducationLevels } from "../../services/educationLevelServices";
import { updateProduct, deleteProduct } from "../../services/productServices";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import editIcon from "../../assets/edit_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import AreYouSureModal from "../../components/AreYouSureModal";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";

const EditBookModal = ({
  isOpen,
  onClose,
  book,
  onBookUpdated,
  setPagination,
}) => {
  const { t } = useTranslation("general");

  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);

  const bookTypes = [
    { title: t("governmental"), value: "governmental" },
    { title: t("enrichment"), value: "enrichment" },
  ];

  const [bookFields, setBookFields] = useState({
    bookName: "",
    bookType: {
      title: "",
      value: "",
    },
    quantity: "",
    academicYear: {
      title: "",
      value: "",
      price: 0,
    },
    price: "",
    purchasePrice: "",
    description: "",
  });

  // Initialize book data when modal opens or book changes
  useEffect(() => {
    if (book && isOpen) {
      setBookFields({
        bookName: book.name || "",
        bookType: {
          title: t(book.productSubType),
          value: book.productSubType || "",
        },
        quantity: book.amountInStock?.toString() || "",
        academicYear: {
          title: book.schoolClass?.name || "", // Access the class name here
          value: book.schoolClassId || "",
          price: book.price || 0,
        },
        price: book.price?.toString() || "",
        purchasePrice: book.purchasePrice?.toString() || "",
        description: book.description || "",
      });

      setIsEditing(false);
      setError(false);
      setErrorMessage("");
    }
  }, [book, isOpen]);

  const fetchAcademicYears = async (
    page = 1,
    limit = 5,
    isDropDown = false,
    searchTerm
  ) => {
    try {
      const response = await getAllEducationLevels({
        page,
        limit,
        search: searchTerm,
      });

      const transformedData = (response.data.classes || []).map((level) => ({
        title: level.name,
        value: level.id,
        price: level.price,
      }));

      if (page === 1) {
        setAcademicYears(transformedData);
      } else {
        setAcademicYears((prev) => [...prev, ...transformedData]);
      }

      return {
        status: response.status || 200,
        data: {
          classes: transformedData,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        },
      };
    } catch (error) {
      console.error("Error fetching academic years:", error);
      setAcademicYears([]);
      return {
        status: 500,
        data: { classes: [], totalPages: 1, totalRecords: 0 },
      };
    }
  };

  const handleInputChange = (e, fieldName) => {
    const value = e.target.value;
    setBookFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
    }));
  };

  const handleUpdateBook = async (e) => {
    e.preventDefault();

    // Validation
    if (
      !bookFields.bookName ||
      !bookFields.bookType.value ||
      !bookFields.quantity ||
      !bookFields.academicYear.value ||
      !bookFields.price ||
      !bookFields.purchasePrice
    ) {
      setError(true);
      setErrorMessage(t("emptyInputFields"));
      return;
    }

    try {
      const updateData = {
        name: bookFields.bookName,
        productSubType: bookFields.bookType.value,
        quantity: parseInt(bookFields.quantity),
        schoolClassId: bookFields.academicYear.value,
        price: parseFloat(bookFields.price),
        purchasePrice: parseFloat(bookFields.purchasePrice),
        description: bookFields.description || null,
        transactionType:
          book.amountInStock !== parseInt(bookFields.quantity) ||
          book.price !== parseFloat(bookFields.price)
            ? "INSTOCK"
            : null,
      };

      setLoadingModal(true);
      const response = await updateProduct(book.id, updateData);

      if (response.status === 200) {
        setError(false);
        setErrorMessage("");
        setIsEditing(false);

        onBookUpdated?.();
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error;
        setError(true);
        setErrorMessage(errorMessage);
      } else {
        console.error("Network or other error:", error.message);
        setError(true);
        setErrorMessage("An unexpected error occurred");
      }
    } finally {
      setLoadingModal(false);
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    setError(false);
    setErrorMessage("");
    onClose();
  };

  const handleCancel = () => {
    if (isEditing) {
      // Reset to original book data
      if (book) {
        setBookFields({
          bookName: book.name || "",
          bookType: {
            title: t(book.productType),
            value: book.productSubType || "",
          },
          quantity: book.amountInStock?.toString() || "",
          academicYear: {
            title: book.schoolClassName || "",
            value: book.schoolClassId || "",
            price: book.price || 0,
          },
          price: book.price?.toString() || "",
          purchasePrice: book.purchasePrice?.toString() || "",
          description: book.description || "",
        });
      }
      setIsEditing(false);
      setError(false);
      setErrorMessage("");
    } else {
      handleClose();
    }
  };

  if (!isOpen || !book) return null;

  return (
    <>
      <Modal
        title={isEditing ? t("editBook") : t("bookDetails")}
        onClose={handleCancel}
        footer={
          <>
            {loadingModal ? <Loader /> : null}
            <section className="edit-user-actions">
              {isEditing ? (
                <>
                  <CustomButton
                    title={t("confirm")}
                    onClick={handleUpdateBook}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={handleCancel}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                </>
              ) : (
                <CustomButton
                  variant="blank"
                  title={t("edit")}
                  onClick={() => setIsEditing(true)}
                  icon={editIcon}
                  iconSize={18}
                />
              )}
            </section>
          </>
        }
      >
        {isEditing ? (
          <form onSubmit={handleUpdateBook}>
            <div>
              {error && (
                <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center">
                  <ErrorBox>{errorMessage}</ErrorBox>
                </div>
              )}
              <div className="edit-user-entity">
                <span>{t("bookName")}: </span>
                <CustomInput
                  name="bookName"
                  value={bookFields.bookName}
                  onChange={(e) => handleInputChange(e, "bookName")}
                  errorText={errorMessage ? t("inputFieldCannotBeEmpty") : ""}
                />

                <span>{t("bookType")}: </span>
                <Dropdown
                  options={bookTypes}
                  selected={{
                    title: t(bookFields.bookType.title),
                    value: bookFields.bookType.value,
                  }}
                  setSelected={(selected) => {
                    setBookFields((prev) => ({
                      ...prev,
                      bookType: {
                        title: selected.title,
                        value: selected.value,
                      },
                    }));
                  }}
                  thick
                />


                <span>{t("academicLevel")}: </span>
                <HotdropdownV2
                  initOptions={[...academicYears]}
                  selected={bookFields.academicYear.title}
                  setSelected={(selected) => {
                    setBookFields((prev) => ({
                      ...prev,
                      academicYear: {
                        title: selected.title,
                        value: selected.value,
                        price: selected.price,
                      },
                    }));
                  }}
                  page="books"
                  getData={(page, limit, isDropDown, query) =>
                    fetchAcademicYears(page, limit, isDropDown, query)
                  }
                  optionsName="classes"
                  thick
                  key="academic-year-dropdown"
                />


                <span>{t("singlePurchasePrice")}: </span>
                <CustomInputWithUnit
                  name="purchasePrice"
                  value={bookFields.purchasePrice}
                  onChange={(e) => handleInputChange(e, "purchasePrice")}
                  type="number"
                  step="0.01"
                  min="0"
                  unit="IQD"
                />
                <span>{t("quantity")}: </span>
                <CustomInputWithUnit
                  name="quantity"
                  value={bookFields.quantity}
                  onChange={(e) => handleInputChange(e, "quantity")}
                  type="number"
                  min="1"
                  unit={t("piece")}
                />
                
                <span>{t("singleSellPrice")}: </span>
                <CustomInputWithUnit
                  name="price"
                  value={bookFields.price}
                  onChange={(e) => handleInputChange(e, "price")}
                  type="number"
                  step="1"
                  min="0"
                  unit="IQD"
                />

                <span>{t("description")}:</span>
                <CustomInput
                  name="description"
                  value={bookFields.description}
                  onChange={(e) => handleInputChange(e, "description")}
                  type="textarea"
                  flex
                />
              </div>
            </div>
          </form>
        ) : (
          <div>
            <div className="view-user-entity">
              <p variant="body1">{t("bookName")}:</p>
              <p variant="body1">{book.name}</p>
            </div>

            <div className="view-user-entity">
              <p variant="body1">{t("bookType")}:</p>
              <p variant="body1">{t(book.productSubType)}</p>
            </div>


            <div className="view-user-entity">
              <p variant="body1">{t("academicLevel")}:</p>
              <p variant="body1">{bookFields?.academicYear?.title}</p>
            </div>

            <div className="view-user-entity">
              <p variant="body1">{t("singlePurchasePrice")}:</p>
              <p variant="body1">{book.purchasePrice} IQD</p>
            </div>

            <div className="view-user-entity">
              <p variant="body1">{t("quantity")}:</p>
              <p variant="body1">
                {book.amountInStock} {t("piece")}
              </p>
            </div>
            
            <div className="view-user-entity">
              <p variant="body1">{t("singleSellPrice")}:</p>
              <p variant="body1">{book.price} IQD</p>
            </div>
            
            {book.description && (
              <div className="view-user-entity">
                <p variant="body1">{t("description")}:</p>
                <p variant="body1">{book.description}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default EditBookModal;
